import React, { Fragment, useEffect, useState } from 'react';
import './App.scss';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUser } from './api/redux/slices/Auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import NavBar from './pages/navigation/index';
import AdminRoutes from './routes/AdminRoutes';
import SuperAdminRoutes from './routes/SuperAdminRoutes';
import CustomerRoutes from './routes/CustomerRoutes';
import SendEmail from './pages/auth/send-email';
import ResetPassword from './pages/auth/reset-password';
import Login from './pages/auth/login';
import Register from './pages/auth/register';
import Landing from './pages/home';
import CodeConfirm from './pages/auth/code-confrim';
import { regionsList } from './api/redux/slices/Public';
import { LoaderComponent } from './helpers/components/loader';

function App() {
	const dispatch = useDispatch();
	const { role, actived, authenticated, error } = useSelector((state) => state.auth);
	const navigate = useNavigate();
	const location = useLocation();
	const token = localStorage.getItem('accesstoken');
	const [userRole, setUserRole] = useState('');
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (token) {
			dispatch(regionsList({ token: token }));
			dispatch(getUser({ token: token }))
				.unwrap()
				.then((res) => {
					setUserRole(res.data.role);
					setIsLoading(false);
				})
				.catch((err) => {
					toast.warn(err, {
						position: 'bottom-right',
						autoClose: 2000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
					});
					localStorage.removeItem('accesstoken');
					setIsLoading(false);
				});
		} else {
			setIsLoading(false);
		}
	}, [dispatch, token, role, actived]);

	// Redirect to login only if:
	// 1. No token exists
	// 2. Trying to access a protected route (not in publicRoutes)
	useEffect(() => {
		// Public routes that don't require authentication - defined inside useEffect
		const publicRoutes = ['/', '/login', '/register', '/send-email', '/code-confirm', '/reset-password'];
		const isPublicRoute = publicRoutes.some(route => location.pathname === route);
		
		if (!token && !isPublicRoute) {
			navigate('/login');
		}
	}, [token, navigate, location.pathname]);

	// Show loader while checking authentication
	if (token && isLoading) {
		return <LoaderComponent />;
	}

	// Show loader if there's an error and still loading
	if (error && isLoading) {
		return <LoaderComponent />;
	}

	// Prevent the keyboard enter action
	document.body.addEventListener('keydown', (e) => {
		if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
			e.preventDefault();
		}
	});

	return (
		<div className='App'>
			<div style={{ position: 'absolute' }}>
				<ToastContainer
					position='top-center'
					autoClose={2000}
					hideProgressBar={false}
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
				/>
			</div>
			<div id='main'>
				{token && <NavBar />}
				<div className={authenticated ? 'rightPanel' : null}>
					{authenticated && (
						<Fragment>
							{userRole === 'admin' && <AdminRoutes />}
							{userRole === 'super-admin' && <SuperAdminRoutes />}
							{userRole === 'customer' && <CustomerRoutes />}
						</Fragment>
					)}
					{!authenticated && (
						<Routes>
							<Route path='/' element={<Landing />} />
							<Route path='/login' element={<Login />} />
							<Route path='/register' element={<Register />} />
							<Route path='/send-email' element={<SendEmail />} />
							<Route path='/code-confirm' element={<CodeConfirm />} />
							<Route path='/reset-password' element={<ResetPassword />} />
						</Routes>
					)}
				</div>
			</div>
		</div>
	);
}

export default App;